import { sqliteTable, text, integer } from "drizzle-orm/sqlite-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { SOURCE_ORIGIN_VALUES } from "./source-origin";

// ---- Pipeline coverage canon constant (W12) ----
// 5X is the locked Mi6 canon target. Use this everywhere — do not hardcode 2.5 / 3 / 4.
export const PIPELINE_COVERAGE_MULTIPLIER = 5;

// =====================================================================
// Mi6 AIP — v3 model
//   v2 base: 4 stages + BANT + 9 checkpoints + 4 buckets
//   v3 adds: pre-gates (G0/G1) + question accordion under each CP +
//            hunt list + industry sub-questions + composite scores
// =====================================================================

// ---- Users (sales reps) ----
export const users = sqliteTable("users", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  email: text("email").notNull().unique(),
  password: text("password").notNull().default(""), // auth is now Supabase Auth; field kept for compat
  name: text("name").notNull(),
  role: text("role").notNull().default("rep"), // "rep" | "manager" | "bdr"
  isDualRole: integer("is_dual_role").notNull().default(0), // 1 for dual-role reps (manager + AE)
  passwordResetRequired: integer("password_reset_required").notNull().default(0),
  supabaseAuthId: text("supabase_auth_id"), // Supabase Auth UUID
});

// ---- Accounts (target companies a rep is pursuing) ----
// Stage = Suspect | Prospect | Qualified Opp | Full Opportunity | Won | Lost
// BANT evidence stored per-letter (null = unconfirmed). Bucket is computed from CPs at read time.
export const accounts = sqliteTable("accounts", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  ownerId: integer("owner_id").notNull(),
  name: text("name").notNull(),
  industry: text("industry").notNull().default(""),
  estArr: integer("est_arr").notNull().default(0),
  stage: text("stage").notNull().default("Suspect"),
  currentState: text("current_state").notNull().default(""),
  goal: text("goal").notNull().default(""),

  // BANT evidence text — null/empty means unconfirmed.
  // Need is the unlock letter (no Need = vendor-shopper, not a buyer).
  bantNeed: text("bant_need"),
  bantBudget: text("bant_budget"),
  bantAuthority: text("bant_authority"),
  bantTimeline: text("bant_timeline"),

  // ---- v3 pre-gates ----
  // G0 = transactional event exists? ("yes" | "no" | null)
  // G1 = new vs. existing customer? ("new" | "existing" | null)
  // customerPath = derived from G1 ("full" | "fastlane" | null)
  g0Answer: text("g0_answer"),
  g1Answer: text("g1_answer"),
  customerPath: text("customer_path"),

  // Cached bucket and prevBucket so manager alerts can detect downgrades cheaply.
  // Computed server-side from checkpoints whenever they change.
  bucket: text("bucket"), // OMITTED | UPSIDE | PROBABLE | COMMIT | null (pre-Stage-4)
  prevBucket: text("prev_bucket"),

  // Score = (greenCps / 9) × 100, kept for backward visual compatibility.
  score: integer("score").notNull().default(0),
  prevScore: integer("prev_score").notNull().default(0),

  updatedAt: integer("updated_at").notNull().default(0),

  // Target close date — sourced from aip_ext.target_close_date ("YYYY-MM-DD").
  // Stored here for type completeness; actual value is read from aip_ext in Supabase storage.
  targetCloseDate: text("target_close_date"),

  // source_origin — how this account entered the system. Set by the AE
  // (or BDR on hand-off). Null = not yet set. Validated against the canonical
  // enum in shared/source-origin.ts at the Zod boundary.
  sourceOrigin: text("source_origin"),
});

// ---- Decision-makers (people inside an account) ----
export const decisionMakers = sqliteTable("decision_makers", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  accountId: integer("account_id").notNull(),
  name: text("name").notNull(),
  title: text("title").notNull().default(""),
  strength: integer("strength").notNull().default(1), // 1-5 relationship strength
});

// ---- Plans (one plan per account, but versioned via plans table) ----
export const plans = sqliteTable("plans", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  accountId: integer("account_id").notNull(),
  version: integer("version").notNull().default(1),
  whereToday: text("where_today").notNull().default(""),
  whatGoodLooksLike: text("what_good_looks_like").notNull().default(""),
  riskFactors: text("risk_factors").notNull().default(""),
  // store action list as JSON: [{action, owner, dueDate}]
  actions: text("actions").notNull().default("[]"),
  // store decision-maker IDs that are "on the map" for this plan
  decisionMakerIds: text("decision_maker_ids").notNull().default("[]"),
  updatedAt: integer("updated_at").notNull().default(0),
});

// ---- Checkpoints (10 per account once the deal hits Full Opportunity) ----
// One row per (accountId, cpNumber). We do NOT auto-seed all 10 on account create —
// they are upserted as the rep scores them, so "unscored" is a real state (no row).
//
// In v3, CP status is DERIVED from question answers (see deriveCpStatus below).
// Reps may still write status manually as an override (`isManual = 1`).
export const checkpoints = sqliteTable("checkpoints", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  accountId: integer("account_id").notNull(),
  cpNumber: integer("cp_number").notNull(), // 1-10
  status: text("status"), // "green" | "amber" | "red" — null means unscored
  note: text("note").notNull().default(""),
  isManual: integer("is_manual").notNull().default(0), // 1 = rep-overridden status
  updatedAt: integer("updated_at").notNull().default(0),
});

// ---- v3: question answers (one row per (account, questionKey)) ----
// `selectedOption` is the option key from the question's option list, or null for unanswered.
// `evidence` is the "I know" text. `isAssumption=1` is the "I think" toggle
//  (which strict-caps its CP at AMBER until evidence is added).
export const accountAnswers = sqliteTable("account_answers", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  accountId: integer("account_id").notNull(),
  questionKey: text("question_key").notNull(),
  selectedOption: text("selected_option"),
  evidence: text("evidence").notNull().default(""),
  isAssumption: integer("is_assumption").notNull().default(0),
  updatedAt: integer("updated_at").notNull().default(0),
});

// ---- v3: hunt list (per-account work items the rep needs to track down) ----
export const huntItems = sqliteTable("hunt_items", {
  id: integer("id").primaryKey({ autoIncrement: true }),
  accountId: integer("account_id").notNull(),
  itemKey: text("item_key").notNull(),
  label: text("label").notNull(),
  status: text("status").notNull().default("open"), // open | chasing | found | blocked
  owner: text("owner").notNull().default(""),
  evidence: text("evidence").notNull().default(""),
  linkedCp: integer("linked_cp"), // CP number this unlocks
  dueDate: text("due_date"),
  industryTrigger: text("industry_trigger"), // null = always-on
  updatedAt: integer("updated_at").notNull().default(0),
});

// ---- Action item shape (validated when stored as JSON in plans.actions) ----
export const actionItemSchema = z.object({
  action: z.string(),
  owner: z.string(),
  dueDate: z.string(), // ISO yyyy-mm-dd or empty string
});
export type ActionItem = z.infer<typeof actionItemSchema>;

// ---- Insert / Update schemas ----
export const insertUserSchema = createInsertSchema(users).omit({ id: true }).extend({
  role: z.enum(["rep", "manager", "bdr"]).default("rep"),
});

// Roles allowed in the system
export const USER_ROLES = ["rep", "manager", "bdr"] as const;
export type UserRole = (typeof USER_ROLES)[number];
// ---- Wizard-fat-payload sub-schemas ----
// Reps fill these out in the new-account wizard. They are persisted to
// `accounts.aip_ext` (jsonb) plus the dedicated `account_answers` /
// `account_cp_status` tables on the server.
export const stakeholderEntrySchema = z.object({
  name: z.string().max(200).optional().default(""),
  role: z.string().max(80).optional().default(""),
  title: z.string().max(200).optional().default(""),
  influence: z.union([z.number(), z.string()]).optional().nullable(),
  support: z.union([z.number(), z.string()]).optional().nullable(),
  engagement: z.union([z.number(), z.string()]).optional().nullable(),
}).passthrough();

export const planActionSchema = z.object({
  action: z.string().max(1000).optional().default(""),
  owner: z.string().max(200).optional().default(""),
  due: z.string().max(40).optional().default(""),
}).passthrough();

export const bantBlockSchema = z.object({
  need: z.string().max(5000).optional().nullable(),
  budget: z.string().max(5000).optional().nullable(),
  authority: z.string().max(5000).optional().nullable(),
  timing: z.string().max(5000).optional().nullable(),
  timeline: z.string().max(5000).optional().nullable(),
}).passthrough();

export const plan30_60_90Schema = z.object({
  day30: z.array(planActionSchema).optional(),
  day60: z.array(planActionSchema).optional(),
  day90: z.array(planActionSchema).optional(),
}).passthrough();

export const aipExtPayloadSchema = z.object({
  bant: bantBlockSchema.optional(),
  mpact: z.any().optional(),
  stakeholders: z.array(stakeholderEntrySchema).optional(),
  risks: z.array(z.string().max(5000)).optional(),
  plan30_60_90: plan30_60_90Schema.optional(),
  current_state: z.string().max(5000).optional(),
  goal: z.string().max(5000).optional(),
  approach: z.string().max(5000).optional(),
  deal_value: z.union([z.number(), z.string()]).optional().nullable(),
  est_arr: z.union([z.number(), z.string()]).optional().nullable(),
  expected_close: z.string().max(40).optional().nullable(),
  next_step: z.string().max(2000).optional().nullable(),
  ce_status: z.string().max(80).optional(),
  ce_description: z.string().max(5000).optional(),
  ce_decision_date: z.string().max(40).optional(),
  ce_confidence: z.union([z.number(), z.string()]).optional().nullable(),
  region: z.string().max(80).optional(),
  employees: z.union([z.number(), z.string()]).optional().nullable(),
}).passthrough();

// TICKET-4: extended with evidence_type / evidence_url / evidence_file fields.
export const cpAnswerPayloadSchema = z.object({
  question_key: z.string().min(1).max(120),
  selected_option: z.string().max(120).optional().nullable(),
  evidence: z.string().max(5000).optional().nullable(),
  is_assumption: z.boolean().optional(),
  answer_notes: z.string().max(5000).optional().nullable(),
  // TICKET-4 evidence fields
  evidence_type: z.enum(["text", "url", "file"]).nullable().optional(),
  evidence_url: z.string().max(2000).optional().nullable(),
  evidence_file_name: z.string().max(500).optional().nullable(),
  evidence_file_size_bytes: z.number().int().max(10485760).optional().nullable(),
}).passthrough();

export const cpStatusPayloadSchema = z.object({
  cp_number: z.number().int().min(1).max(10),
  status: z.enum(["green", "amber", "red"]).nullable(),
  note: z.string().max(5000).optional().nullable(),
  is_manual: z.boolean().optional(),
}).passthrough();

// P0-3: enforce text-length caps on every free-form field to prevent
// 50,000-char pastes from reaching storage. Mirrors client INPUT_LIMITS.
// Mi6 AIP is a manufacturing-only platform — the question bank, templates,
// and coaching are built for industrial/hardgoods manufacturers. The canonical
// list of allowed industries is exported from client/src/lib/industries.ts;
// duplicated here so server validation does not import client code.
export const APPROVED_INDUSTRIES = [
  "Industrial Manufacturing",
  "Automotive",
  "Aerospace & Defense",
  "Energy / Utilities",
  "Consumer Goods Manufacturing",
  "Building Products / Construction Materials",
  "Metals & Mining",
  "Chemicals & Plastics",
  "Food & Beverage Manufacturing",
] as const;
export type ApprovedIndustry = (typeof APPROVED_INDUSTRIES)[number];

export function isApprovedIndustry(value: string | null | undefined): boolean {
  if (!value) return true; // blank/null is allowed (legacy + initial wizard state)
  return (APPROVED_INDUSTRIES as readonly string[]).includes(value);
}

// TODO(2026-05-13 strict-enum): swap industry on insertAccountSchema to
//   z.enum(APPROVED_INDUSTRIES).optional()
// once telemetry from the soft-warn period (logged in server routes) confirms
// no caller writes a non-approved string. Manufacturing-only sweep — phase 1.

export const insertAccountSchema = createInsertSchema(accounts).omit({
  id: true,
  score: true,
  prevScore: true,
  bucket: true,
  prevBucket: true,
  updatedAt: true,
}).extend({
  name:           z.string().min(1).max(200, "Name too long (max 200 chars)"),
  industry:       z.string().max(200, "Industry too long (max 200 chars)").optional().default(""),
  currentState:   z.string().max(5000, "Current state too long (max 5000 chars)").optional().default(""),
  goal:           z.string().max(5000, "Goal too long (max 5000 chars)").optional().default(""),
  bantNeed:       z.string().max(5000).optional().nullable(),
  bantBudget:     z.string().max(5000).optional().nullable(),
  bantAuthority:  z.string().max(5000).optional().nullable(),
  bantTimeline:   z.string().max(5000).optional().nullable(),

  // Fat wizard payload — persisted to accounts.aip_ext + child tables.
  // Optional so that legacy POSTs (just {name, industry, stage, estArr})
  // continue to work unchanged.
  aip_ext:        aipExtPayloadSchema.optional(),
  cp_answers:     z.array(cpAnswerPayloadSchema).optional(),
  cp_statuses:    z.array(cpStatusPayloadSchema).optional(),

  // source_origin — null means "not yet set". The 4 enum values are the only
  // accepted strings; any other value is rejected with 400. AEs populate this
  // as they go.
  sourceOrigin: z.enum(SOURCE_ORIGIN_VALUES).nullable().optional(),
});
export const updateAccountSchema = insertAccountSchema.partial();

export const insertDecisionMakerSchema = createInsertSchema(decisionMakers).omit({ id: true });
export const updateDecisionMakerSchema = insertDecisionMakerSchema.partial();

export const insertPlanSchema = createInsertSchema(plans).omit({
  id: true,
  version: true,
  updatedAt: true,
});
export const updatePlanSchema = insertPlanSchema.partial();

export const upsertCheckpointSchema = z.object({
  cpNumber: z.number().int().min(1).max(10),
  status: z.enum(["green", "amber", "red"]).nullable(),
  note: z.string().optional(),
  isManual: z.boolean().optional(),
});

// TICKET-4: extended with evidence_type / evidence_url / evidence_file fields.
// evidenceType=null means no evidence (I Think / assumption).
// evidenceType='text' requires evidence string >= 20 chars (enforced server-side).
// evidenceType='url' requires evidenceUrl starting with 'https://' (enforced server-side).
// evidenceType='file' requires a preceding upload via POST /api/accounts/:id/evidence-upload.
export const upsertAnswerSchema = z.object({
  questionKey: z.string().min(1),
  selectedOption: z.string().nullable(),
  evidence: z.string().max(5000).optional(),
  isAssumption: z.boolean().optional(),
  // TICKET-4 evidence fields
  evidenceType: z.enum(["text", "url", "file"]).nullable().optional(),
  evidenceUrl: z.string().max(2000).optional().nullable(),
  evidenceFileName: z.string().max(500).optional().nullable(),
  evidenceFileSizeBytes: z.number().int().max(10485760).optional().nullable(),
});

export const upsertHuntItemSchema = z.object({
  itemKey: z.string().min(1),
  label: z.string().min(1),
  status: z.enum(["open", "chasing", "found", "blocked"]).optional(),
  owner: z.string().optional(),
  evidence: z.string().optional(),
  linkedCp: z.number().int().min(1).max(9).nullable().optional(),
  dueDate: z.string().nullable().optional(),
  industryTrigger: z.string().nullable().optional(),
});

// ---- Types ----
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type InsertAccount = z.infer<typeof insertAccountSchema>;
export type UpdateAccount = z.infer<typeof updateAccountSchema>;
export type Account = typeof accounts.$inferSelect;
export type InsertDecisionMaker = z.infer<typeof insertDecisionMakerSchema>;
export type DecisionMaker = typeof decisionMakers.$inferSelect;
export type InsertPlan = z.infer<typeof insertPlanSchema>;
export type UpdatePlan = z.infer<typeof updatePlanSchema>;
export type Plan = typeof plans.$inferSelect;
export type Checkpoint = typeof checkpoints.$inferSelect;
export type UpsertCheckpoint = z.infer<typeof upsertCheckpointSchema>;
export type AccountAnswer = typeof accountAnswers.$inferSelect;
export type UpsertAnswer = z.infer<typeof upsertAnswerSchema>;
export type HuntItem = typeof huntItems.$inferSelect;
export type UpsertHuntItem = z.infer<typeof upsertHuntItemSchema>;

// ---- Stages (v2 — 4 active + Won + Lost) ----
export const STAGES = [
  "Suspect",
  "Prospect",
  "Qualified Opp",
  "Full Opportunity",
  "Won",
  "Lost",
] as const;
export type Stage = (typeof STAGES)[number];

// Stage weights for weighted-pipeline math (rough, replaces v1 7-stage weights).
export const STAGE_WEIGHTS: Record<Stage, number> = {
  Suspect: 0.05,
  Prospect: 0.15,
  "Qualified Opp": 0.4,
  "Full Opportunity": 0.7,
  Won: 1,
  Lost: 0,
};

// ---- BANT ----
export const BANT_LETTERS = ["Need", "Budget", "Authority", "Timeline"] as const;
export type BantLetter = (typeof BANT_LETTERS)[number];

export interface BantState {
  Need: { confirmed: boolean; evidence: string };
  Budget: { confirmed: boolean; evidence: string };
  Authority: { confirmed: boolean; evidence: string };
  Timeline: { confirmed: boolean; evidence: string };
}

export function readBant(account: Pick<Account, "bantNeed" | "bantBudget" | "bantAuthority" | "bantTimeline">): BantState {
  const cell = (s: string | null | undefined) => ({
    confirmed: !!(s && s.trim().length > 0),
    evidence: s ?? "",
  });
  return {
    Need: cell(account.bantNeed),
    Budget: cell(account.bantBudget),
    Authority: cell(account.bantAuthority),
    Timeline: cell(account.bantTimeline),
  };
}

export function bantConfirmedCount(b: BantState): number {
  return (Number(b.Need.confirmed) + Number(b.Budget.confirmed) + Number(b.Authority.confirmed) + Number(b.Timeline.confirmed));
}

// ---- CP1 Verdict logic (BDR BANT qualifier) ----
//
// Philosophy (Bryan, 2026-05-06):
//   A deal can ENTER the funnel with just ONE BANT letter confirmed.
//   The AE's job through CP2–CP10 is to fill in the remaining letters or
//   kill / sideline the opportunity. Every downstream gate continuously
//   re-tests whether to qualify or disqualify.
//
// FORWARD  — at least 1 BANT letter confirmed AND no hard-kill hit.
// ELIMINATED — any hard-kill hit OR zero BANT letters confirmed.
// HOLD     — zero BANT letters confirmed AND a soft-kill was hit
//            (not a hard out, but not enough signal to forward either).
export type Cp1Verdict = "FORWARD" | "HOLD" | "ELIMINATED";

export function cp1Verdict(args: {
  bant: BantState;
  hardKillHit: boolean;
  softKillHit: boolean;
}): { verdict: Cp1Verdict; reason: string } {
  const { bant, hardKillHit, softKillHit } = args;

  const confirmedCount = bantConfirmedCount(bant);

  // ELIMINATED: any hard-kill hit — name the specific cause
  if (hardKillHit) {
    return {
      verdict: "ELIMINATED",
      reason: "Eliminated: industry out of lane or no project confirmed — hard disqualifier hit.",
    };
  }

  // ELIMINATED: zero BANT letters confirmed (and no soft-kill justifying a Hold)
  if (confirmedCount === 0 && !softKillHit) {
    return {
      verdict: "ELIMINATED",
      reason: "Eliminated: zero BANT letters confirmed after first-touch discovery — not enough signal to enter the funnel.",
    };
  }

  // HOLD: zero BANT confirmed but soft-kill hit — not a hard out, keep working
  if (confirmedCount === 0 && softKillHit) {
    return {
      verdict: "HOLD",
      reason: "Held for further discovery — soft signals only, no BANT letter yet confirmed.",
    };
  }

  // FORWARD: at least 1 BANT letter confirmed, no hard-kill
  const confirmed: string[] = [];
  if (bant.Need.confirmed) confirmed.push("Need");
  if (bant.Budget.confirmed) confirmed.push("Budget");
  if (bant.Authority.confirmed) confirmed.push("Authority");
  if (bant.Timeline.confirmed) confirmed.push("Timeline");

  const missingLetters = [
    !bant.Need.confirmed ? "Need" : null,
    !bant.Budget.confirmed ? "Budget" : null,
    !bant.Authority.confirmed ? "Authority" : null,
    !bant.Timeline.confirmed ? "Timeline" : null,
  ].filter(Boolean) as string[];

  const missingPhrase =
    missingLetters.length > 0
      ? ` (work continues to confirm ${missingLetters.join(", ")})`
      : "";

  return {
    verdict: "FORWARD",
    reason: `Forwarded: ${confirmed.join(" + ")} confirmed${missingPhrase}.`,
  };
}

// ---- Stage gating: validate a transition with BANT/CPs ----
// Returns { ok: true } or { ok: false, reason: "..." }.
export function validateStageTransition(args: {
  fromStage: Stage;
  toStage: Stage;
  bant: BantState;
  scoredCpCount: number; // count of CPs with status set (any of green/amber/red)
}): { ok: true } | { ok: false; reason: string } {
  const { toStage, bant, scoredCpCount } = args;
  // Won / Lost can be set from anywhere
  if (toStage === "Won" || toStage === "Lost") return { ok: true };
  // Suspect requires nothing
  if (toStage === "Suspect") return { ok: true };
  // Prospect requires Need confirmed (1 BANT, must include Need)
  if (toStage === "Prospect") {
    if (!bant.Need.confirmed) return { ok: false, reason: "Prospect requires Need confirmed (the unlock letter)." };
    return { ok: true };
  }
  // Qualified Opp requires Need + 1 more (≥2 BANT including Need)
  if (toStage === "Qualified Opp") {
    if (!bant.Need.confirmed) return { ok: false, reason: "Qualified Opp requires Need confirmed." };
    if (bantConfirmedCount(bant) < 2) return { ok: false, reason: "Qualified Opp requires at least 2 BANT letters (including Need)." };
    return { ok: true };
  }
  // Full Opportunity requires Need + ≥3 BANT AND ≥3 of 10 CPs scored to start the bucket
  if (toStage === "Full Opportunity") {
    if (!bant.Need.confirmed) return { ok: false, reason: "Full Opportunity requires Need confirmed." };
    if (bantConfirmedCount(bant) < 3) return { ok: false, reason: "Full Opportunity requires at least 3 BANT letters (including Need)." };
    if (scoredCpCount < 3) return { ok: false, reason: "Full Opportunity requires at least 3 of the 10 checkpoints scored." };
    return { ok: true };
  }
  return { ok: true };
}

// ---- Checkpoints (v3 canonical names) ----
// 10-gate model (locked 2026-05-06 per Bryan).
// CP1/2/3/6/7/10 are hard-kill; CP4/5/8/9 are soft.
// CP6 Demonstration is hard-kill CONDITIONAL: only when buyer reports a must-have gap got worse after demo.
export interface CheckpointDef {
  number: number;
  label: string;
  hardKill: boolean;
  category: "hard" | "soft";
  description: string;
}
// CHECKPOINT_DEFS — locked CP10 taxonomy (2026-05-06 per Bryan).
// Mirrors client/src/lib/helix-checkpoints.ts. Source of truth for the
// server-side checkpoint payload returned to clients.
export const CHECKPOINT_DEFS: CheckpointDef[] = [
  {
    number: 1,
    label: "Project",
    hardKill: true,
    category: "hard",
    description: "Is there a real project — named initiative, sponsor, budget cycle. New logo or expansion both count.",
  },
  {
    number: 2,
    label: "Need / Pain / Demonstration",
    hardKill: true,
    category: "hard",
    description: "Pain named, quantified, in her own words. Cost of doing nothing for the next twelve months.",
  },
  {
    number: 3,
    label: "Budget",
    hardKill: true,
    category: "hard",
    description: "Real money, named source, validated line item. Owner named, threshold known. Paired with CP4 Timeline.",
  },
  {
    number: 4,
    label: "Budget",
    hardKill: true,
    category: "hard",
    description: "Real money, named source, validated line item. Owner named, threshold known.",
  },
  {
    number: 5,
    label: "Timeline",
    hardKill: false,
    category: "soft",
    description: "Decision window AND implementation window — both real, both confirmed in writing.",
  },
  {
    number: 6,
    label: "Authority",
    hardKill: true,
    category: "hard",
    description: "Who actually signs. Economic, technical, security, legal — named, met, dated.",
  },
  {
    number: 7,
    label: "Org Structure and Influence Map",
    hardKill: false,
    category: "soft",
    description: "Champions, supporters, influencers, detractors. Political, personal, and professional agendas understood. Project Lead identified and validated.",
  },
  {
    number: 8,
    label: "Competition",
    hardKill: false,
    category: "soft",
    description: "Where you stand vs alternatives in her head, evaluated not assumed. Do-nothing option named.",
  },
  {
    number: 9,
    label: "Compliance",
    hardKill: true,
    category: "hard",
    description: "IT internal and industry-specific compliance inventory, owned and planned. Mutual delivery plan documented with bilateral acknowledgment.",
  },
  {
    number: 10,
    label: "Legal",
    hardKill: true,
    category: "hard",
    description: "MSA, redlines, contract terms, paper path open. Counsel named on both sides.",
  },
];
// Locked CP10 taxonomy (label refresh 2026-05-25 per master spec):
//   Hard-kill: CP1 Project, CP2 Need/Pain/Demonstration, CP3 Budget,
//              CP4 Budget(legacy slot), CP6 Authority, CP9 Compliance, CP10 Legal
//   Soft:      CP5 Timeline, CP7 Org Structure and Influence Map, CP8 Competition
// Note: numeric hard/soft mapping unchanged — only display labels were renamed
// for the pilot launch. Full taxonomy rewire (e.g. swapping CP3/CP4 slots, adding
// Lead stage) is tracked separately and deferred.
export const HARD_KILL_CPS = [1, 2, 3, 4, 6, 9, 10] as const;
export const SOFT_CPS = [5, 7, 8] as const;

// Fast-lane (existing-customer expansion) — TICKET-3 canonical skip list
// Locked 2026-05-16 per spec TICKET-3-gate0-qualifier-routing.md.
// Updated 2026-05-16 11:17 PM EDT per Bryan: CP7 is NOT skipped for existing
// customers. Existing customers still have people-mapping risk (management
// changes, approval shifts, champion turnover). CP7 stays active but its
// question set is modified for the fastlane to focus on change-detection
// rather than discovery (see FASTLANE_CP7_MODE below).
//
// Source: AIP v2 gate logic (gates 3,4,8 skip for existing customer).
//   Skipped:  CP3 Budget, CP4 Budget(legacy)/Timeline, CP8 Competition
//   Required: CP1 Project, CP2 Need/Pain/Demonstration, CP5 Timeline, CP6 Authority,
//             CP7 Org Structure and Influence Map (modified), CP9 Compliance, CP10 Legal
export const FASTLANE_SKIPPED_CPS = [3, 4, 8] as const;
export const FASTLANE_REQUIRED_CPS = [1, 2, 5, 6, 7, 9, 10] as const;

// When customerPath === 'fastlane' AND cp === 7, present the change-detection
// question subset rather than the full people-mapping discovery flow.
// Question bank consumers must check this flag before rendering CP7.
export const FASTLANE_CP7_MODE = "change-detection" as const;
export type Cp7Mode = "discovery" | "change-detection";
export function cp7ModeForPath(customerPath: string | null | undefined): Cp7Mode {
  return customerPath === "fastlane" ? "change-detection" : "discovery";
}

export function isCpActiveForPath(cpNumber: number, customerPath: string | null | undefined): boolean {
  if (customerPath !== "fastlane") return true;
  return !FASTLANE_SKIPPED_CPS.includes(cpNumber as (typeof FASTLANE_SKIPPED_CPS)[number]);
}

// ---- Buckets ----
export const BUCKETS = ["OMITTED", "UPSIDE", "PROBABLE", "COMMIT"] as const;
export type Bucket = (typeof BUCKETS)[number];

export type CpStatus = "green" | "amber" | "red" | null;

// Pure function: given the 10 CP statuses, return the bucket — or null when pre-stage-4 (no scoring yet).
// `cpStatus` is a map from CP number (1-10) to status ("green"|"amber"|"red"|null/missing).
export function computeBucket(cpStatus: Record<number, CpStatus>): Bucket | null {
  // If nothing scored yet, bucket is undetermined.
  const anyScored = HARD_KILL_CPS.some((n) => cpStatus[n]) || SOFT_CPS.some((n) => cpStatus[n]);
  if (!anyScored) return null;
  // OMITTED if any hard-kill is red
  if (HARD_KILL_CPS.some((n) => cpStatus[n] === "red")) return "OMITTED";
  // COMMIT if all 10 are green
  const allTen = [...HARD_KILL_CPS, ...SOFT_CPS];
  if (allTen.every((n) => cpStatus[n] === "green")) return "COMMIT";
  // PROBABLE: all 6 hard-kills green AND ≥3 of 4 soft green
  const hardAllGreen = HARD_KILL_CPS.every((n) => cpStatus[n] === "green");
  const softGreenCount = SOFT_CPS.filter((n) => cpStatus[n] === "green").length;
  if (hardAllGreen && softGreenCount >= 3) return "PROBABLE";
  // Otherwise UPSIDE (no hard-kill red, but doesn't meet PROBABLE/COMMIT bar)
  return "UPSIDE";
}

// Score = (greenCpCount / 10) * 100, rounded. Used for backward compat with old /100 displays.
export function computeCpScore(cpStatus: Record<number, CpStatus>): number {
  const allTen = [...HARD_KILL_CPS, ...SOFT_CPS];
  const greens = allTen.filter((n) => cpStatus[n] === "green").length;
  return Math.round((greens / 10) * 100);
}

// Count of CPs that have any status set (used to gate Stage 4).
export function scoredCpCount(cpStatus: Record<number, CpStatus>): number {
  const allTen = [...HARD_KILL_CPS, ...SOFT_CPS];
  return allTen.filter((n) => cpStatus[n] !== null && cpStatus[n] !== undefined).length;
}

// ---- Canonical health palette (CANON_VISUALS_LOCKED.md, locked 2026-05-27) ----
// Single source of truth. Do NOT duplicate this table in component code.
// 5 states: gray (not scored) / red (bad) / orange (concern) / yellow (caution) / green (good).
// Score thresholds: <0 (or null/unscored) handled by getHealthColor(null).
export const HEALTH_PALETTE = {
  gray:   '#6B7280',
  red:    '#DC2626',
  orange: '#EA580C',
  yellow: '#EAB308',
  green:  '#16A34A',
  // ---- Legacy aliases (deprecated 2026-05-27) ----
  // Old 5-band 'no gray' keys now map to canon 5-state colors. Callers should
  // migrate to the new keys above. These exist only so the wave-4 deploy ships
  // without a 11-file simultaneous rewrite.
  darkGreen:  '#16A34A', // was '#1B7A3A' — now canon green
  lightGreen: '#16A34A', // was '#5BB95B' — collapsed into canon green
  amber:      '#EAB308', // was '#E0A82E' — now canon yellow
  orangeRed:  '#EA580C', // was '#D76B30' — now canon orange
  darkRed:    '#DC2626', // was '#9B1B1B' — now canon red
} as const;

export type HealthColorKey = 'gray' | 'red' | 'orange' | 'yellow' | 'green';

export function getHealthColorKey(score: number | null | undefined): HealthColorKey {
  if (score === null || score === undefined || Number.isNaN(score)) return 'gray';
  if (score >= 80) return 'green';
  if (score >= 60) return 'yellow';
  if (score >= 40) return 'orange';
  if (score > 0)   return 'red';
  return 'gray';
}

export function getHealthColor(score: number | null | undefined): string {
  return HEALTH_PALETTE[getHealthColorKey(score)];
}

export function getForecastCategory(score: number): 'Commit' | 'Upside' | 'Best Case' | 'Pipeline' | 'Omit' {
  if (score >= 80) return 'Commit';
  if (score >= 65) return 'Upside';
  if (score >= 50) return 'Best Case';
  if (score >= 30) return 'Pipeline';
  return 'Omit';
}
